import { Button } from '@app/components/common/buttons/Button/Button';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import * as S from './PriceQuote.styles';
import { Tooltip } from 'antd';
import { useInView } from '@app/hooks/useInView';
import { getFileWithSignedUrl } from '@app/api/file.api';
import { isFileImage, isFilePdf, openFile } from '@app/utils/file';
import { IFile } from '@app/interfaces/file';

pdfjs.GlobalWorkerOptions.workerSrc = 'assets/js/pdf.worker.min.js';

interface PriceQuoteProps {
  open: boolean;
  file: string;
  pageStart?: number;
  pageEnd?: number;
}

export const PriceQuote: React.FC<PriceQuoteProps> = ({ file, open, pageStart, pageEnd }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(window.innerWidth);
  const containerRef = useRef<HTMLDivElement>(null);
  // Every document panel is open at once now, so each PDF waits for its own
  // panel to approach the viewport rather than all of them fetching on load.
  const inView = useInView(containerRef);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isFileUrl, setIsFileUrl] = useState(false);
  const [showPage, setShowPage] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const [fileObject, setFileObject] = useState<IFile | null>(null);

  const openDocumentFile = () => {
    if (isFileUrl) {
      window.open(file as string, '_blank', 'noopener,noreferrer');
      return;
    }
    openFile(file);
  };

  const downloadDocumentFile = () => {
    openFile(file);
  };

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }) => {
      setError(null);
      if (pageStart && pageEnd) {
        setPageNumber(pageStart);
        setNumPages(pageEnd);
        setShowPage(pageStart !== pageEnd);
      } else {
        setShowPage(true);
        setNumPages(numPages);
      }
      setLoading(false);
    },
    [pageEnd, pageStart],
  );

  const onDocumentLoadError = useCallback(() => {
    setError(t('dealRooms.sections.failedToLoad'));
    setLoading(false);
  }, [t]);

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  }, [numPages]);

  useEffect(() => {
    if (fileUrl && isPdf) {
      // Destroy all canvases created by PDF.js
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach((canvas) => {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      });

      // Optionally remove from DOM (optional)
      const layers = document.querySelectorAll('.react-pdf__Page__canvas');
      layers.forEach((layer) => layer.remove());
      setLoadedUrl(fileUrl as string);
    }
  }, [fileUrl, isPdf]);

  useEffect(() => {
    const fetchFile = async (id: string) => {
      try {
        const file = await getFileWithSignedUrl(id);
        setFileUrl(file?.signedUrl as string);
        setFileObject(file);
        const isImage = isFileImage(file?.originalName as string);
        const isPdf = isFilePdf(file?.originalName as string);
        setIsPdf(isPdf);
        setIsImage(isImage);
        if (!isPdf) setLoading(false);
      } catch (error) {
        setError((error as Error).message);
      }
    };

    if (file) {
      const isUrl = typeof file === 'string' && (file.startsWith('http://') || file.startsWith('https://'));
      if (isUrl) {
        setFileUrl(file);
        setIsFileUrl(true);
        setIsPdf(isFilePdf(file));
        setIsImage(isFileImage(file));
      } else {
        fetchFile(file as string);
      }
    }
  }, [file]);

  useEffect(() => {
    const handleResize = () => {
      // Measured off this panel's own node: every open panel carries the same
      // `price-quote` id, so getElementById would size them all from the first.
      const container = containerRef.current;
      if (container) {
        setWidth(container.offsetWidth - 60);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [inView]);

  return (
    <>
      {open && (
        <S.QuoteContainer id="price-quote" ref={containerRef} $awaitingView={!inView}>
          {inView && (
            <Spinner spinning={loading}>
              {isImage && fileUrl && !isPdf ? (
                <S.ImagePreview src={fileUrl} alt={fileObject?.originalName || 'image'} />
              ) : null}
              {fileUrl && !isPdf && fileObject ? (
                <S.FooterWrapper>
                  <S.ButtonContainer>
                    <S.FileName>{fileObject?.originalName}</S.FileName>
                  </S.ButtonContainer>

                  {(!pageStart || pageStart === 0) && (
                    <Tooltip title={t('dealRooms.sections.viewDocument')}>
                      <S.DocumentIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadDocumentFile();
                        }}
                      />
                    </Tooltip>
                  )}
                </S.FooterWrapper>
              ) : null}
              {isPdf && (
                <>
                  <S.FooterWrapper className="top">
                    {!error && (
                      <>
                        {showPage && (
                          <S.ButtonContainer>
                            <Button type="primary" onClick={goToPrevPage} disabled={pageNumber <= (pageStart || 1)}>
                              {t('dealRooms.sections.previous')}
                            </Button>
                            <S.ButtonText>
                              {t('dealRooms.sections.pageOf', {
                                page: pageNumber,
                                totalPages: numPages,
                              })}
                            </S.ButtonText>
                            <Button
                              type="primary"
                              onClick={goToNextPage}
                              disabled={numPages ? pageNumber >= numPages : true}
                            >
                              {t('dealRooms.sections.next')}
                            </Button>
                          </S.ButtonContainer>
                        )}
                        {(!pageStart || pageStart === 0) && (
                          <S.DocumentLink
                            onClick={(e) => {
                              e.stopPropagation();
                              openDocumentFile();
                            }}
                          >
                            {t('dealRooms.sections.downloadDocumentAsPdf', 'הורד מסמך כ - PDF')}
                          </S.DocumentLink>
                        )}
                      </>
                    )}
                  </S.FooterWrapper>
                  <Document
                    className={'dyno-document'}
                    file={loadedUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={<S.LoadingMessage>{t('dealRooms.sections.loadingPDf')}</S.LoadingMessage>}
                    error={null}
                  >
                    {!error && (
                      <S.PageContainer
                        key={`${pageNumber}-${Date.now()}`}
                        pageNumber={pageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        className="shadow-lg"
                        width={width}
                      />
                    )}
                  </Document>
                  <S.FooterWrapper>
                    {error && <S.ErrorMessage>{error}</S.ErrorMessage>}
                    {showPage && !error && (
                      <S.ButtonContainer>
                        <Button type="primary" onClick={goToPrevPage} disabled={pageNumber <= (pageStart || 1)}>
                          {t('dealRooms.sections.previous')}
                        </Button>
                        <S.ButtonText>
                          {t('dealRooms.sections.pageOf', {
                            page: pageNumber,
                            totalPages: numPages,
                          })}
                        </S.ButtonText>
                        <Button
                          type="primary"
                          onClick={goToNextPage}
                          disabled={numPages ? pageNumber >= numPages : true}
                        >
                          {t('dealRooms.sections.next')}
                        </Button>
                      </S.ButtonContainer>
                    )}

                    {(!pageStart || pageStart === 0) && (
                      <S.DocumentLink
                        onClick={(e) => {
                          e.stopPropagation();
                          openDocumentFile();
                        }}
                      >
                        {t('dealRooms.sections.downloadDocumentAsPdf', 'הורד מסמך כ - PDF')}
                      </S.DocumentLink>
                    )}
                  </S.FooterWrapper>
                </>
              )}
            </Spinner>
          )}
        </S.QuoteContainer>
      )}
    </>
  );
};
